import type {
  AdminCreateCardPoolInput,
  AdminCreateProductInput,
  AdminCreateVariantInput,
  AdminDashboardSummary,
  AdminConfigureVariantDeliveryInput,
  AdminGenerateSecretsInput,
  AdminImportSecretsInput,
  AdminImportSecretsResult,
  AdminProductRecord,
  AdminSortInput,
  AdminSession,
  AdminUpdateCardPoolInput,
  AdminUpdateProductInput,
  AdminUpdateVariantInput,
  AdminUploadImageInput,
  AdminUploadImageResult,
  CardPool,
  CreateOrderInput,
  OrderRecord,
  PaymentSession,
  PaymentSettingsSummary,
  PublicSiteSettingsSummary,
  PublicPaymentChannelsSummary,
  PublicProduct,
  SystemSettingsSummary,
  UpdatePaymentSettingsInput,
  UpdateSystemSettingsInput,
  InstallSetupInput,
  InstallStatusSummary,
} from '@starkey/shared'
import axios, { AxiosError } from 'axios'
import { clearAdminSession } from './adminSession'

const client = axios.create({
  baseURL: '/api',
})

type ApiEnvelope<T> = {
  code: number
  message: string
  data: T
}

function normalizeApiError(error: unknown) {
  if (axios.isAxiosError<ApiEnvelope<unknown>>(error)) {
    const responseMessage = error.response?.data?.message
    if (typeof responseMessage === 'string' && responseMessage.trim()) {
      const normalizedMessage = tryNormalizeZodArrayMessage(responseMessage)
      return new Error(normalizedMessage || responseMessage)
    }

    if (error.code === AxiosError.ERR_NETWORK || !error.response) {
      return new Error('网络连接失败，请稍后重试')
    }

    return new Error(`请求失败 (${error.response.status})`)
  }

  return error instanceof Error ? error : new Error('请求失败，请稍后重试')
}

function tryNormalizeZodArrayMessage(message: string) {
  const content = message.trim()
  if (!content.startsWith('[')) {
    return ''
  }

  try {
    const payload = JSON.parse(content) as Array<{ message?: string }>
    return payload
      .map(item => item.message?.trim())
      .filter(Boolean)
      .join('，')
  } catch {
    return ''
  }
}

function isAdminAuthFailure(error: AxiosError<ApiEnvelope<unknown>>) {
  const requestUrl = typeof error.config?.url === 'string' ? error.config.url : ''
  return error.response?.status === 401 && requestUrl.startsWith('/admin/') && requestUrl !== '/admin/auth/login'
}

client.interceptors.response.use(
  response => response,
  error => {
    if (axios.isAxiosError<ApiEnvelope<unknown>>(error) && isAdminAuthFailure(error)) {
      clearAdminSession()
      if (typeof window !== 'undefined' && window.location.pathname !== '/admin/login') {
        window.location.replace('/admin/login')
      }
    }

    return Promise.reject(normalizeApiError(error))
  }
)

async function unwrap<T>(request: Promise<{ data: ApiEnvelope<T> }>) {
  const response = await request
  if (response.data.code !== 0) {
    throw new Error(response.data.message)
  }

  return response.data.data
}

function createAdminHeaders(token: string) {
  return {
    Authorization: `Bearer ${token}`,
  }
}

function sanitizePaymentSettingsPayload(payload: UpdatePaymentSettingsInput): UpdatePaymentSettingsInput {
  const nextPayload: UpdatePaymentSettingsInput = {
    ...payload,
  }

  const optionalSecretKeys: Array<
    'alipayPrivateKey' | 'alipayPublicKey' | 'wechatMerchantPrivateKey' | 'wechatApiV3Key' | 'wechatPlatformPublicKey'
  > = ['alipayPrivateKey', 'alipayPublicKey', 'wechatMerchantPrivateKey', 'wechatApiV3Key', 'wechatPlatformPublicKey']

  for (const key of optionalSecretKeys) {
    const value = nextPayload[key]
    if (typeof value !== 'string' || value.trim() === '') {
      delete nextPayload[key]
    } else {
      nextPayload[key] = value.trim() as never
    }
  }

  return nextPayload
}

export const publicApi = {
  listProducts: () => unwrap<PublicProduct[]>(client.get('/public/products')),
  getProduct: (slug: string) => unwrap<PublicProduct>(client.get(`/public/products/${slug}`)),
  getPaymentChannels: () => unwrap<PublicPaymentChannelsSummary>(client.get('/public/payment-channels')),
  getSiteSettings: () => unwrap<PublicSiteSettingsSummary>(client.get('/public/site-settings')),
  createOrder: (payload: CreateOrderInput) => unwrap<OrderRecord>(client.post('/public/orders', payload)),
  createPayment: (orderNo: string, channel: 'ALIPAY_PAGE' | 'WECHAT_NATIVE', returnOrigin?: string) =>
    unwrap<PaymentSession>(client.post(`/public/orders/${orderNo}/payments`, { channel, returnOrigin })),
  queryOrders: (phone: string, email: string) =>
    unwrap<OrderRecord[]>(client.get('/public/orders/query', { params: { phone, email } })),
  getOrderDetail: (orderNo: string, phone: string, email: string) =>
    unwrap<OrderRecord>(client.get(`/public/orders/${orderNo}`, { params: { phone, email } })),
}

export const systemApi = {
  getInstallStatus: () => unwrap<InstallStatusSummary>(client.get('/system/install-status')),
}

export const installApi = {
  install: (payload: InstallSetupInput) => unwrap<InstallStatusSummary>(client.post('/install', payload)),
}

export const adminApi = {
  login: (username: string, password: string) =>
    unwrap<AdminSession>(client.post('/admin/auth/login', { username, password })),
  getDashboard: (token: string, range: AdminDashboardSummary['range']) =>
    unwrap<AdminDashboardSummary>(client.get('/admin/dashboard', { headers: createAdminHeaders(token), params: { range } })),
  listProducts: (token: string) =>
    unwrap<AdminProductRecord[]>(client.get('/admin/products', { headers: createAdminHeaders(token) })),
  createProduct: (token: string, payload: AdminCreateProductInput) =>
    unwrap<AdminProductRecord>(client.post('/admin/products', payload, { headers: createAdminHeaders(token) })),
  updateProduct: (token: string, productId: string, payload: AdminUpdateProductInput) =>
    unwrap<AdminProductRecord>(client.put(`/admin/products/${productId}`, payload, { headers: createAdminHeaders(token) })),
  reorderProducts: (token: string, payload: AdminSortInput) =>
    unwrap<AdminProductRecord[]>(client.put('/admin/products/reorder', payload, { headers: createAdminHeaders(token) })),
  deleteProduct: (token: string, productId: string) =>
    unwrap<boolean>(client.delete(`/admin/products/${productId}`, { headers: createAdminHeaders(token) })),
  createVariant: (token: string, productId: string, payload: AdminCreateVariantInput) =>
    unwrap<AdminProductRecord>(
      client.post(`/admin/products/${productId}/variants`, payload, { headers: createAdminHeaders(token) })
    ),
  updateVariant: (token: string, variantId: string, payload: AdminUpdateVariantInput) =>
    unwrap<AdminProductRecord>(client.put(`/admin/variants/${variantId}`, payload, { headers: createAdminHeaders(token) })),
  updateVariantDelivery: (token: string, variantId: string, payload: AdminConfigureVariantDeliveryInput) =>
    unwrap<AdminProductRecord>(
      client.put(`/admin/variants/${variantId}/delivery`, payload, { headers: createAdminHeaders(token) })
    ),
  reorderVariants: (token: string, productId: string, payload: AdminSortInput) =>
    unwrap<AdminProductRecord>(
      client.put(`/admin/products/${productId}/variants/reorder`, payload, { headers: createAdminHeaders(token) })
    ),
  deleteVariant: (token: string, variantId: string) =>
    unwrap<AdminProductRecord>(client.delete(`/admin/variants/${variantId}`, { headers: createAdminHeaders(token) })),
  listOrders: (token: string) =>
    unwrap<OrderRecord[]>(client.get('/admin/orders', { headers: createAdminHeaders(token) })),
  markOrderPaid: (token: string, orderNo: string) =>
    unwrap<OrderRecord>(client.post(`/admin/orders/${orderNo}/mark-paid`, null, { headers: createAdminHeaders(token) })),
  listCardPools: (token: string) =>
    unwrap<CardPool[]>(client.get('/admin/card-pools', { headers: createAdminHeaders(token) })),
  createCardPool: (token: string, payload: AdminCreateCardPoolInput) =>
    unwrap<CardPool>(client.post('/admin/card-pools', payload, { headers: createAdminHeaders(token) })),
  updateCardPool: (token: string, cardPoolId: string, payload: AdminUpdateCardPoolInput) =>
    unwrap<CardPool>(client.put(`/admin/card-pools/${cardPoolId}`, payload, { headers: createAdminHeaders(token) })),
  deleteCardPool: (token: string, cardPoolId: string) =>
    unwrap<boolean>(client.delete(`/admin/card-pools/${cardPoolId}`, { headers: createAdminHeaders(token) })),
  generateSecrets: (token: string, cardPoolId: string, payload: AdminGenerateSecretsInput) =>
    unwrap<CardPool>(client.post(`/admin/card-pools/${cardPoolId}/generate`, payload, { headers: createAdminHeaders(token) })),
  importSecrets: (token: string, cardPoolId: string, payload: AdminImportSecretsInput) =>
    unwrap<AdminImportSecretsResult>(
      client.post(`/admin/card-pools/${cardPoolId}/import`, payload, { headers: createAdminHeaders(token) })
    ),
  deleteCardSecret: (token: string, cardPoolId: string, secretId: string) =>
    unwrap<CardPool>(client.delete(`/admin/card-pools/${cardPoolId}/secrets/${secretId}`, { headers: createAdminHeaders(token) })),
  getPaymentSettings: (token: string) =>
    unwrap<PaymentSettingsSummary>(client.get('/admin/settings/payments', { headers: createAdminHeaders(token) })),
  updatePaymentSettings: (token: string, payload: UpdatePaymentSettingsInput) =>
    unwrap<PaymentSettingsSummary>(
      client.put('/admin/settings/payments', sanitizePaymentSettingsPayload(payload), {
        headers: createAdminHeaders(token),
      })
    ),
  getSystemSettings: (token: string) =>
    unwrap<SystemSettingsSummary>(client.get('/admin/settings/system', { headers: createAdminHeaders(token) })),
  updateSystemSettings: (token: string, payload: UpdateSystemSettingsInput) =>
    unwrap<SystemSettingsSummary>(client.put('/admin/settings/system', payload, { headers: createAdminHeaders(token) })),
  uploadImage: (token: string, payload: AdminUploadImageInput) =>
    unwrap<AdminUploadImageResult>(client.post('/admin/uploads/images', payload, { headers: createAdminHeaders(token) })),
}
